import * as React from "react";
import styles from "./OfficeRequests.module.scss";
import {
    ClinicianOfficeWindowRequests,
    DateFormats,
    IClinician,
    IOfficeWindow,
    IOfficeWindowSchedule,
    OfficeRequestStatus,
    SignatureData,
    TOffices,
} from "intake_shared/src/types";
import {
    addSpaceToString,
    getStringFromScheduleDate,
    hasConflictingAvailability,
    hasConflictingAvailabilityWithCliniciansRemoteSchedules,
    sortStringsWithNumberEndings,
    toTitleCase,
} from "../../../../utils/utils";
import store from "../../../../store";
import { useToasts } from "react-toast-notifications";
import { AclContext } from "../../../../contexts/AclContext";
import { v4 as uuidv4 } from "uuid";
import { Dropdown } from "../../bits/Dropdown/Dropdown";
import { Button } from "../../bits/Button/Button";
import ChevronDownIcon from "../../icons/ChevronDown";
import moment from "moment";
import { TableHeader } from "../../../../types";
import { OFFICE_REQUEST_STATUS_COLORS } from "../../../../constants";
import Table, { TablePagination } from "../../bits/Table/Table";
import FilterButton from "../../bits/FilterButton/FilterButton";
import RefreshIcon from "../../icons/Refresh";
import { SearchInput } from "../../bits/SearchInput/SearchInput";
import { uniq } from "lodash";
import { Badge } from "../../bits/Badge/Badge";
import { Loader } from "../../bits/Loader/Loader";
import { CheckAvailabilityDialog } from "../CheckAvailabilityDialog/CheckAvailabilityDialog";
import { RejectRequestDialog } from "../RejectRequestDialog/RejectRequestDialog";
import EmptyListState from "../../bits/EmptyListState/EmptyListState";
import { OverlayTrigger, Popover, Tooltip } from "react-bootstrap";
import { CacheContext } from "../../../../contexts/CacheContext";
import { getIntakeEmailSignature } from "../../../../utils/intakeEmailSignature";

export const DAYS = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
];

interface OfficeRequestsProps {
    setOfficeRequestCount: (value: React.SetStateAction<number>) => void;
    officeManagerLocations: string[] | undefined;
    filteredOffices: TOffices;
}

export default function OfficeRequests({
    setOfficeRequestCount,
    officeManagerLocations,
    filteredOffices,
}: OfficeRequestsProps) {
    const { addToast } = useToasts();
    const olc = React.useContext(CacheContext);
    const intakeSignature = getIntakeEmailSignature(olc);
    const [actionRow, setActionRow] =
        React.useState<ClinicianOfficeWindowRequests | null>(null);
    const [officeWindowRequests, setOfficeWindowRequests] =
        React.useState<ClinicianOfficeWindowRequests[]>();
    const loggedInUserEmail = React.useContext(AclContext)?.authUser.user?.email;
    const [rowData, setRowData] = React.useState<ClinicianOfficeWindowRequests>();
    const [refreshKey, setRefreshKey] = React.useState(0);
    const [selectedStatus, setSelectedStatus] = React.useState(
        OfficeRequestStatus.PENDING
    );
    const [currentPage, setCurrentPage] = React.useState(1);
    const pageLimit = 50;
    const [loading, setLoading] = React.useState(false);
    const [showDeletePopUp, setShowDeletePopUp] = React.useState(false);
    const [nextButtonDisabled, setNextButtonDisabled] = React.useState(false);
    const [conflict, setConflict] = React.useState<IOfficeWindowSchedule>();
    const [hasRemoteConflict, setHasRemoteConflict] =
        React.useState<IOfficeWindowSchedule>();
    const [clinicianToFind, setClincianToFind] = React.useState("");
    const [selectedLocation, setSelectedLocation] = React.useState("");
    const [selectedDay, setSelectedDay] = React.useState("");
    const [selectedOfficeNumber, setSelectedOfficeNumber] = React.useState("");
    const [showCheckAvaliablity, setShowCheckAvaliablity] = React.useState(false);

    const [draftApproveWithChanges, setDraftApproveWithChanges] = React.useState({
        from: "8:00",
        to: "9:00",
        reason: "",
        office: "",
    });
    const [clinicianToApproveData, setClinicianToApproveData] =
        React.useState<IClinician>([] as unknown as IClinician);

    const [officeRequestDetails, setOfficeRequestDetails] = React.useState({
        clinician_name: "",
        start_date: new Date(),
        end_date: new Date(),
        location: "",
        office: "",
        id: "",
        requester: "",
        category: "",
    });

    const [requestToApproveWithChanges, setRequestToApproveWithChanges] =
        React.useState({
            clinician_name: "",
            start_date: new Date(),
            end_date: new Date(),
            location: "",
            office: "",
            id: "",
            requester: "",
        });

    const getClinicianOfficeRequests = async () => {
        const filters = {
            location: selectedLocation,
            clinician_name: clinicianToFind.toLowerCase(),
            office: selectedOfficeNumber,
            status: selectedStatus,
            day: selectedDay,
        };
        setLoading(true);

        try {
            const requests = await store.findOfficeWindowRequestsByCategory(
                loggedInUserEmail as string,
                "request_list",
                currentPage,
                pageLimit,
                filters
            );
            setOfficeRequestCount(
                requests?.filter(
                    (request) => request.status === OfficeRequestStatus.PENDING
                ).length
            );
            setOfficeWindowRequests(requests);
            setLoading(false);
            // Check if there is more data for the next page
            setNextButtonDisabled(requests?.length < pageLimit);
        } catch (error) {
            setLoading(false);
            // Log the exact error to the console
            console.error("An error occurred:", error);
            // Show the toast to the user
            addToast(
                "Your current role does not include office management responsibilities.",
                {
                    appearance: "error",
                    autoDismiss: false,
                }
            );
        }
    };

    React.useEffect(() => {
        getClinicianOfficeRequests();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        refreshKey,
        selectedLocation,
        selectedOfficeNumber,
        clinicianToFind,
        selectedDay,
        selectedStatus,
        currentPage,
    ]);

    const handleCheckAvailability = async (row: ClinicianOfficeWindowRequests) => {
        let schedules: IOfficeWindowSchedule[] = [];
        const clinicianList = await store.fetchAllCliniciansByLocation(row.location);

        clinicianList.forEach((c) => {
            schedules = schedules.concat(
                c.office_windows
                    .filter((w) => w.location === row.location)
                    .map((w) => {
                        return {
                            clinicianId: c.id,
                            clinicianEmail: c.email,
                            id: w.id,
                            assigned_to: `${c.firstname} ${c.lastname}`,
                            start: w.start,
                            end: w.end,
                            ending_date: w.ending_date,
                            office: w.office,
                            location: row.location,
                        };
                    })
            );
        });
        const heldRequest = await store.getAllHeldOfficeWindowsByLocation(
            row.location
        );
        const filteredRequest = heldRequest.map((item) => {
            return {
                clinicianId: item.clinician_name,
                start: item.start_date,
                end: item.end_date,
                location: item.location,
                office: item.office,
            };
        });

        const conflict = await hasConflictingAvailability(
            [...schedules, ...filteredRequest],
            row.start_date.toISOString(),
            row.end_date.toISOString(),
            row.office,
            row.location,
            loggedInUserEmail as string
        );

        setConflict(conflict);

        const request = await store.getClinicianByEmail(row.requester);

        const clincianToApproveRemoteWindows = request?.office_windows.filter(
            (window) => window.location === "Remote"
        );
        const requestHasConflictWithClinicianRemoteSchedules =
            await hasConflictingAvailabilityWithCliniciansRemoteSchedules(
                clincianToApproveRemoteWindows as IOfficeWindow[],
                row.start_date.toISOString(),
                row.end_date.toISOString()
            );

        setHasRemoteConflict(requestHasConflictWithClinicianRemoteSchedules);

        const getClinicianDetails = async () => {
            const request = await store.getClinicianByEmail(row?.requester);
            setClinicianToApproveData(request as IClinician);
        };
        getClinicianDetails();
        setShowCheckAvaliablity(true);
    };

    const handleUnholdWindow = async (
        status: OfficeRequestStatus,
        row?: ClinicianOfficeWindowRequests
    ) => {
        setLoading(true);
        // unhold office window so its now accessible to everyone
        const success = await store.handleHoldOfficeSpace(
            status,
            row?.office_window_request_id as string,
            "hold_window",
            "",
            row?.start_date as Date,
            row?.end_date as Date,
            row?.office || "",
            row?.requester || "",
            row?.location || "",
            row?.requester || ""
        );

        if (success) {
            addToast("Office window unheld successfully", {
                appearance: "success",
            });
            setRefreshKey((prevKey) => prevKey + 1);
        }
        setLoading(false);
    };

    const handleApproveOfficeWindow = async (row: ClinicianOfficeWindowRequests) => {
        setLoading(true);

        const bookingData = {
            id: uuidv4(),
            clinicianId: row.requester,
            office: row.office,
            location: row.location,
            start: getStringFromScheduleDate(row.start_date),
            end: getStringFromScheduleDate(row.end_date),
            ending_date:
                row.ending_date && getStringFromScheduleDate(row.ending_date),
            status: OfficeRequestStatus.APPROVED,
            assigned_to: row.clinician_name as string,
            available: false,
            office_window_request_id: row.office_window_request_id as string,
            office_manager: loggedInUserEmail as string,
            category: row.category,
            intakeSignature: intakeSignature || ({} as SignatureData),
        };
        await store.approveClinicianOfficeWindow(bookingData);
        setRefreshKey((prevKey) => prevKey + 1);
        setLoading(false);

        setShowCheckAvaliablity(false);
        addToast("Office window saved", {
            appearance: "success",
        });
    };

    const ActionsCell: React.FC<{ row: ClinicianOfficeWindowRequests }> = ({
        row,
    }) => {
        if (row.status !== OfficeRequestStatus.REJECTED) {
            return (
                <OverlayTrigger
                    trigger="click"
                    placement="bottom-end"
                    flip
                    rootClose
                    show={actionRow === row}
                    onToggle={(show) => setActionRow(show ? row : null)}
                    overlay={
                        <Popover
                            id="office-request-row-actions"
                            className={styles.actionsPopover}
                            aria-label="Row actions"
                        >
                            <Popover.Content
                                className={styles.actionsDropdownContent}
                                onClick={() => setActionRow(null)}
                            >
                                {row.status === OfficeRequestStatus.PENDING && (
                                    <>
                                        <Button
                                            variant="normal"
                                            type="button"
                                            onClick={async () => {
                                                setRowData(row);
                                                setRequestToApproveWithChanges({
                                                    ...requestToApproveWithChanges,
                                                    clinician_name:
                                                        row.clinician_name as string,
                                                    start_date:
                                                        row.start_date as Date,
                                                    end_date: row.end_date as Date,
                                                    location: row.location,
                                                    office: row.office,
                                                    id: row.office_window_request_id,
                                                    requester: row.requester,
                                                });
                                                setDraftApproveWithChanges({
                                                    ...draftApproveWithChanges,
                                                    from: moment(
                                                        row?.start_date
                                                    ).format(
                                                        DateFormats.HOUR_MIN_FORMAT
                                                    ) as string,
                                                    to: moment(row?.end_date).format(
                                                        DateFormats.HOUR_MIN_FORMAT
                                                    ) as string,
                                                    reason: "",
                                                    office: row?.office,
                                                });
                                                setOfficeRequestDetails({
                                                    ...officeRequestDetails,
                                                    clinician_name:
                                                        row.clinician_name as string,
                                                    start_date:
                                                        row.start_date as Date,
                                                    end_date: row.end_date as Date,
                                                    location: row.location,
                                                    office: row.office,
                                                    id: row.office_window_request_id,
                                                    requester: row.requester,
                                                    category: row.category || "",
                                                });

                                                handleCheckAvailability(row);
                                            }}
                                        >
                                            Check Availability
                                        </Button>
                                    </>
                                )}
                                {(row.status === OfficeRequestStatus.APPROVED ||
                                    row.status ===
                                        OfficeRequestStatus.APPROVED_WITH_CHANGES) && (
                                    <Button
                                        variant="normal"
                                        type="button"
                                        onClick={() => {
                                            setShowDeletePopUp(true);
                                            setOfficeRequestDetails({
                                                ...officeRequestDetails,
                                                clinician_name:
                                                    row.clinician_name as string,
                                                start_date: row.start_date as Date,
                                                end_date: row.end_date as Date,
                                                location: row.location,
                                                office: row.office,
                                                id: row.office_window_request_id,
                                                requester: row.requester,
                                                category: row.category || "",
                                            });
                                        }}
                                    >
                                        Delete Window
                                    </Button>
                                )}
                                {row.status === OfficeRequestStatus.HELD && (
                                    <>
                                        <Button
                                            variant="normal"
                                            type="button"
                                            onClick={async () =>
                                                handleApproveOfficeWindow(row)
                                            }
                                        >
                                            Approve Window
                                        </Button>
                                        <Button
                                            variant="normal"
                                            type="button"
                                            onClick={() => {
                                                handleUnholdWindow(
                                                    OfficeRequestStatus.PENDING,
                                                    row
                                                );
                                            }}
                                        >
                                            Unhold Window
                                        </Button>
                                        <Button
                                            variant="normal"
                                            type="button"
                                            onClick={() => {
                                                setShowDeletePopUp(true);
                                                setOfficeRequestDetails({
                                                    ...officeRequestDetails,
                                                    clinician_name:
                                                        row.clinician_name as string,
                                                    start_date:
                                                        row.start_date as Date,
                                                    end_date: row.end_date as Date,
                                                    location: row.location,
                                                    office: row.office,
                                                    id: row.office_window_request_id,
                                                    requester: row.requester,
                                                    category: row.category || "",
                                                });
                                            }}
                                        >
                                            Delete Window
                                        </Button>
                                    </>
                                )}
                            </Popover.Content>
                        </Popover>
                    }
                >
                    <Button
                        aria-expanded={actionRow === row}
                        iconRight={<ChevronDownIcon stroke="var(--color-white)" />}
                    >
                        Actions
                    </Button>
                </OverlayTrigger>
            );
        }
        return null;
    };

    const AvailableCell: React.FC<{ row: ClinicianOfficeWindowRequests }> = ({
        row,
    }) => {
        if (row.available === false || row.available === true) {
            return <p>{row.available === true ? "Yes" : "No"}</p>;
        }
        return <span>--</span>;
    };

    const columns = React.useMemo(() => {
        const headers: TableHeader[] = [
            {
                Header: "CLINICIAN",
                id: "clinician_name",
                accessor: (row: ClinicianOfficeWindowRequests) => (
                    <p className="caseLdClName">{row.clinician_name || ""}</p>
                ),
            },
            {
                Header: "REQUEST DATE",
                id: "",
                accessor: (row: ClinicianOfficeWindowRequests) => (
                    <p className="caseLdClName">{`${moment(row.request_date).format(
                        DateFormats.DAY_YEAR_HOUR_MIN_FORMAT
                    )}`}</p>
                ),
            },
            {
                Header: "REQUESTED DAY",
                id: "",
                accessor: (row: ClinicianOfficeWindowRequests) => (
                    <p className="caseLdClName">{`${moment(row.start_date).format(
                        DateFormats.DAY
                    )}`}</p>
                ),
            },
            {
                Header: "REQUESTED TIME",
                id: "start_date",
                accessor: (row: ClinicianOfficeWindowRequests) => (
                    <p className="caseLdClName">{`${moment(row.start_date).format(
                        DateFormats.HOUR_MIN_FORMAT
                    )} - ${moment(row.end_date).format(
                        DateFormats.HOUR_MIN_FORMAT
                    )}`}</p>
                ),
            },
            {
                Header: "Delivery OPTION",
                id: "delivery_option",
                accessor: (row: ClinicianOfficeWindowRequests) => (
                    <p>
                        {toTitleCase(row?.delivery_option)?.replace("_", " ") ||
                            "--"}
                    </p>
                ),
            },

            {
                Header: "LOCATION",
                id: "location",
                accessor: (row: ClinicianOfficeWindowRequests) => (
                    <p>{row.location || ""}</p>
                ),
            },
            {
                Header: "OFFICE NO",
                id: "office_number",
                accessor: (row: ClinicianOfficeWindowRequests) => (
                    <p>{row.office || ""}</p>
                ),
            },
            {
                Header: "REOCCURRENCE",
                id: "recurrence",
                accessor: (row: ClinicianOfficeWindowRequests) => (
                    <p>{row.recurrence || ""}</p>
                ),
            },
            {
                Header: "AVAILABLE",
                id: "available",
                accessor: (row: ClinicianOfficeWindowRequests) => {
                    return <AvailableCell row={row} />;
                },
            },
            {
                Header: "ASSIGNED TO",
                id: "assigned_to",
                accessor: (row: ClinicianOfficeWindowRequests) => (
                    <p>{row?.assigned_to ? row?.assigned_to : "--"}</p>
                ),
            },
            {
                Header: "REQUEST COUNT",
                id: "request_list_count",
                accessor: (row: ClinicianOfficeWindowRequests) => (
                    <p>{row.request_list_count || "--"}</p>
                ),
            },
            {
                Header: "STATUS",
                id: "status",
                accessor: (row: ClinicianOfficeWindowRequests) => (
                    <Badge
                        bgColor={OFFICE_REQUEST_STATUS_COLORS[row.status || ""]}
                        content={addSpaceToString(row.status as string) || ""}
                        textColor="var(--color-white)"
                    />
                ),
            },
            {
                Header: "COMMENTS",
                id: "comments",
                accessor: (row: ClinicianOfficeWindowRequests) => {
                    const comments: Array<{
                        role: string;
                        type: string;
                        comment: string;
                    }> = [];

                    // Add all available comments with their roles
                    if (row.change_end_time_reason) {
                        comments.push({
                            role: "Clinician",
                            type: "Reason for Changing Duration",
                            comment: row.change_end_time_reason,
                        });
                    }

                    if (row.approve_with_changes_reason) {
                        comments.push({
                            role: "Office Manager",
                            type: "Reason for Approving with Changes",
                            comment: row.approve_with_changes_reason,
                        });
                    }

                    if (row.rejection_reason) {
                        comments.push({
                            role: "Office Manager",
                            type: "Reason for Rejection",
                            comment: row.rejection_reason,
                        });
                    }

                    if (row.reason_for_holding) {
                        comments.push({
                            role: "Office Manager",
                            type: "Reason for Holding",
                            comment: row.reason_for_holding,
                        });
                    }

                    if (row.comment) {
                        comments.push({
                            role: "Clinician",
                            type: "General Comment",
                            comment: row.comment,
                        });
                    }

                    // Display all comments
                    return (
                        <div>
                            {comments && comments.length > 0 ? (
                                <OverlayTrigger
                                    placement="bottom"
                                    overlay={
                                        <Tooltip id="comments-tooltip">
                                            {comments.map((item, index) => (
                                                <div
                                                    key={index}
                                                    style={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        gap: "4px",
                                                    }}
                                                >
                                                    <div style={{ display: "flex" }}>
                                                        {item?.role}
                                                        {item?.type
                                                            ? `: ${item?.type}`
                                                            : ""}
                                                    </div>
                                                    <div>{item?.comment}</div>
                                                </div>
                                            ))}
                                        </Tooltip>
                                    }
                                >
                                    <span>
                                        {comments[0]?.role}:{" "}
                                        {comments[0]?.comment?.length > 4
                                            ? comments[0]?.comment?.substring(0, 4) +
                                              "...."
                                            : comments[0]?.comment}
                                    </span>
                                </OverlayTrigger>
                            ) : (
                                "--"
                            )}
                        </div>
                    );
                },
            },
            {
                Header: "Actions",
                id: "actions",
                accessor: (row: ClinicianOfficeWindowRequests) => {
                    return <ActionsCell row={row} />;
                },
            },
        ];
        return headers;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [actionRow]);

    const handleResetFilters = () => {
        setSelectedLocation("");
        setSelectedOfficeNumber("");
        setSelectedStatus("" as OfficeRequestStatus);
        setClincianToFind("");
        setCurrentPage(1);
    };

    const handleFirstPageClick = () => {
        setCurrentPage(1);
    };

    const handlePrevClick = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleNextClick = () => {
        setCurrentPage(currentPage + 1);
    };

    const tableFooter = (
        <TablePagination
            currentPage={currentPage}
            onFirstPage={handleFirstPageClick}
            onPrevPage={handlePrevClick}
            onNextPage={handleNextClick}
            isNextDisabled={nextButtonDisabled}
            showPrevButton={currentPage > 1}
        />
    );

    const getLocationLabel = (location: string) => {
        return location || "Select location";
    };

    const getOfficeLabel = (office: string) => {
        return office || "Select office";
    };

    const getStatusLabel = (status: string) => {
        return addSpaceToString(status) || "Select status";
    };

    const getDayLabel = (day: string) => {
        return day || "Select day";
    };

    return (
        <div>
            <Loader show={loading} />
            <CheckAvailabilityDialog
                showCheckAvailability={showCheckAvaliablity}
                setShowCheckAvailability={setShowCheckAvaliablity}
                row={rowData as ClinicianOfficeWindowRequests}
                conflict={(conflict || hasRemoteConflict) as IOfficeWindowSchedule}
                setRefreshKey={setRefreshKey}
                requestToApproveWithChanges={requestToApproveWithChanges}
                setDraftApproveWithChanges={setDraftApproveWithChanges}
                draftApproveWithChanges={draftApproveWithChanges}
                officeRequestToReject={officeRequestDetails}
                loggedInUserEmail={loggedInUserEmail as string}
                clinicianToApproveData={clinicianToApproveData}
                officeDetailsToHold={officeRequestDetails}
            />
            <RejectRequestDialog
                rejectRequest={showDeletePopUp}
                officeRequestToReject={officeRequestDetails}
                setRejectRequest={setShowDeletePopUp}
                setRefreshKey={setRefreshKey}
                requestToRejectId={officeRequestDetails.id}
                loggedInUserEmail={loggedInUserEmail as string}
                viewFrom={"office_request_list"}
            />
            <div className="office-manager-filters">
                <SearchInput
                    placeholder="Clinician Name"
                    value={clinicianToFind}
                    onChange={(e) => setClincianToFind(e.target.value)}
                />
                <Dropdown>
                    <Dropdown.Trigger>
                        <FilterButton text={getStatusLabel(selectedStatus)} />
                    </Dropdown.Trigger>
                    <Dropdown.Content width="190px">
                        <Dropdown.Item
                            closeMenuOnSelect
                            isSelected={
                                selectedStatus === ("" as OfficeRequestStatus)
                            }
                            onClick={() =>
                                setSelectedStatus("" as OfficeRequestStatus)
                            }
                        >
                            Show all
                        </Dropdown.Item>
                        {Object.values(OfficeRequestStatus)
                            .filter((s) => s !== OfficeRequestStatus.ON_WAITLIST)
                            .map((s) => (
                                <Dropdown.Item
                                    key={`status-${s}`}
                                    closeMenuOnSelect
                                    isSelected={selectedStatus === s}
                                    onClick={() => setSelectedStatus(s)}
                                >
                                    {addSpaceToString(s)}
                                </Dropdown.Item>
                            ))}
                    </Dropdown.Content>
                </Dropdown>

                <Dropdown>
                    <Dropdown.Trigger>
                        <FilterButton text={getDayLabel(selectedDay)} />
                    </Dropdown.Trigger>
                    <Dropdown.Content width="190px">
                        <Dropdown.Item
                            closeMenuOnSelect
                            isSelected={selectedDay === ("" as string)}
                            onClick={() => setSelectedDay("" as string)}
                        >
                            Show all
                        </Dropdown.Item>
                        {Object.values(DAYS).map((day) => (
                            <Dropdown.Item
                                key={day}
                                closeMenuOnSelect
                                isSelected={selectedDay === day}
                                onClick={() => setSelectedDay(day)}
                            >
                                {day}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Content>
                </Dropdown>
                <Dropdown>
                    <Dropdown.Trigger>
                        <FilterButton text={getLocationLabel(selectedLocation)} />
                    </Dropdown.Trigger>

                    <Dropdown.Content width="170px">
                        <Dropdown.Item
                            closeMenuOnSelect
                            isSelected={selectedLocation === ""}
                            onClick={() => setSelectedLocation("")}
                        >
                            Show all
                        </Dropdown.Item>
                        {officeManagerLocations?.map((locations) => (
                            <Dropdown.Item
                                key={locations}
                                closeMenuOnSelect
                                isSelected={selectedLocation === locations}
                                onClick={() => setSelectedLocation(locations)}
                            >
                                {locations}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Content>
                </Dropdown>
                <Dropdown>
                    <Dropdown.Trigger>
                        <FilterButton text={getOfficeLabel(selectedOfficeNumber)} />
                    </Dropdown.Trigger>
                    <Dropdown.Content>
                        <Dropdown.Item
                            closeMenuOnSelect
                            isSelected={selectedOfficeNumber === ""}
                            onClick={() => setSelectedOfficeNumber("")}
                        >
                            Show all
                        </Dropdown.Item>
                        {sortStringsWithNumberEndings(
                            uniq(filteredOffices[selectedLocation] || [])
                        ).map((r) => (
                            <Dropdown.Item
                                key={r}
                                onClick={() => setSelectedOfficeNumber(r)}
                                isSelected={selectedOfficeNumber === r}
                            >
                                {r}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Content>
                </Dropdown>
                <Button onClick={() => setRefreshKey((prevKey) => prevKey + 1)}>
                    <RefreshIcon />
                </Button>
                <Button
                    variant="normal"
                    className="reset-filters-button"
                    onClick={handleResetFilters}
                >
                    Reset Filters
                </Button>
            </div>
            {officeWindowRequests &&
                (officeWindowRequests.length === 0 ? (
                    <EmptyListState
                        emptyMessage="No office window requests"
                        stateHelperText="Office window requests will be shown here"
                    />
                ) : (
                    <Table
                        stickyLastColumn
                        columns={columns}
                        data={officeWindowRequests}
                        allowSelect={false}
                        footer={tableFooter}
                    />
                ))}
        </div>
    );
}
